import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { Users, UserPlus, Mail, Shield, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface TeamMember {
  id: string;
  user_id: string;
  role: 'admin' | 'moderator' | 'user';
  created_at: string;
  profile?: {
    email: string;
    full_name: string;
  };
  beta_config?: {
    beta_tier: string;
    is_active: boolean;
  };
}

const TeamMemberManagement = () => {
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'moderator' | 'user'>('user');
  const [inviteBetaTier, setInviteBetaTier] = useState<'free' | 'premium' | 'unlimited'>('free');

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      
      // Fetch all user roles with profile data
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select(`
          *,
          profiles!user_roles_user_id_fkey (
            email,
            full_name
          )
        `)
        .order('created_at', { ascending: false });

      if (rolesError) throw rolesError;

      // Fetch beta configs for these users
      const userIds = userRoles.map(r => r.user_id);
      const { data: betaConfigs, error: betaError } = await supabase
        .from('beta_user_config')
        .select('user_id, beta_tier, is_active')
        .in('user_id', userIds);

      if (betaError) {
        console.warn('Error fetching beta configs:', betaError);
      }

      // Combine data
      const membersWithData = userRoles.map(role => ({
        ...role,
        profile: (role as any).profiles?.[0] || null,
        beta_config: betaConfigs?.find(config => config.user_id === role.user_id) || null
      }));

      setMembers(membersWithData as TeamMember[]);
    } catch (error: any) {
      console.error('Error fetching team members:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const updateMemberRole = async (userId: string, newRole: 'admin' | 'moderator' | 'user') => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .update({ role: newRole })
        .eq('user_id', userId);

      if (error) throw error;

      toast.success('Member role updated successfully');
      fetchTeamMembers();
    } catch (error: any) {
      console.error('Error updating member role:', error);
      toast.error('Failed to update member role');
    }
  };

  const removeMember = async (userId: string) => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId);

      if (error) throw error;

      toast.success('Member removed successfully');
      fetchTeamMembers();
    } catch (error: any) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove member');
    }
  };

  const inviteTeamMember = async () => {
    try {
      // Step 1: Check if user already exists
      const { data: existingUser, error: userError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', inviteEmail)
        .single();

      if (userError && userError.code !== 'PGRST116') {
        throw userError;
      }

      if (existingUser) {
        // User exists - check if they already have a role
        const { data: existingRole, error: roleError } = await supabase
          .from('user_roles')
          .select('id')
          .eq('user_id', existingUser.id)
          .single();

        if (roleError && roleError.code !== 'PGRST116') {
          throw roleError;
        }

        if (existingRole) {
          toast.error('User already has access to the platform');
          return;
        }

        // Add role to existing user
        const { error: insertRoleError } = await supabase
          .from('user_roles')
          .insert([{ user_id: existingUser.id, role: inviteRole }]);

        if (insertRoleError) throw insertRoleError;

        // Update their beta config
        const { error: betaError } = await supabase
          .from('beta_user_config')
          .upsert([{
            user_id: existingUser.id,
            beta_tier: inviteBetaTier,
            ai_credits_remaining: inviteBetaTier === 'free' ? 100 : inviteBetaTier === 'premium' ? 500 : 999999,
            daily_limit: inviteBetaTier === 'free' ? 10 : inviteBetaTier === 'premium' ? 50 : 999999,
            monthly_budget_cents: inviteBetaTier === 'free' ? 500 : inviteBetaTier === 'premium' ? 2000 : 999999
          }]);

        if (betaError) throw betaError;

        toast.success('Access granted to existing user');
      } else {
        // For new users, we'll just show instructions since we can't create auth users from client
        toast.info(`To add ${inviteEmail}:\n1. Ask them to sign up at your app\n2. Come back here to assign their role`);
      }

      setInviteEmail('');
      setInviteRole('user');
      setInviteBetaTier('free');
      setIsInviteDialogOpen(false);
      fetchTeamMembers();
    } catch (error: any) {
      console.error('Error inviting team member:', error);
      toast.error('Failed to invite team member');
    }
  };

  const getBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin': return 'destructive';
      case 'moderator': return 'default';
      case 'user': return 'secondary';
      default: return 'secondary';
    }
  };

  const filteredMembers = members.filter(member => {
    const matchesSearch = !searchTerm || 
      member.profile?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.profile?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.user_id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = filterRole === 'all' || member.role === filterRole;
    
    return matchesSearch && matchesRole;
  });

  const roleStats = {
    total: members.length,
    admin: members.filter(m => m.role === 'admin').length,
    moderator: members.filter(m => m.role === 'moderator').length,
    user: members.filter(m => m.role === 'user').length
  };

  useEffect(() => {
    if (user && isAdmin) {
      fetchTeamMembers();
    }
  }, [user, isAdmin]);

  if (!isAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Access Denied</CardTitle>
          <CardDescription>You need admin privileges to manage team members.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Members</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{roleStats.total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Admins</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{roleStats.admin}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Moderators</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{roleStats.moderator}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{roleStats.user}</div>
          </CardContent>
        </Card>
      </div>

      {/* Team Member Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Team Member Management</CardTitle>
              <CardDescription>Manage team member roles and access levels</CardDescription>
            </div>
            <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Member
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Team Member</DialogTitle>
                  <DialogDescription>
                    Grant access to a team member. If they haven't signed up yet, ask them to create an account first.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="teammate@company.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="role">Role</Label>
                    <Select value={inviteRole} onValueChange={(value) => setInviteRole(value as any)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="moderator">Moderator</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="tier">Beta Tier</Label>
                    <Select value={inviteBetaTier} onValueChange={(value) => setInviteBetaTier(value as any)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="premium">Premium</SelectItem>
                        <SelectItem value="unlimited">Unlimited</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={inviteTeamMember} className="flex-1">
                      <Mail className="h-4 w-4 mr-2" />
                      Grant Access
                    </Button>
                    <Button variant="outline" onClick={() => setIsInviteDialogOpen(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mt-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="moderator">Moderator</SelectItem>
                <SelectItem value="user">User</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading team members...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Member</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Beta Tier</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {member.profile?.full_name || 'Unknown User'}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {member.profile?.email || member.user_id}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getBadgeVariant(member.role)}>
                        {member.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {member.beta_config?.beta_tier || 'Not Set'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={member.beta_config?.is_active ? 'default' : 'secondary'}>
                        {member.beta_config?.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Select
                          value={member.role}
                          onValueChange={(value) => updateMemberRole(member.user_id, value as any)}
                          disabled={member.user_id === user?.id} // Can't change own role
                        >
                          <SelectTrigger className="w-[120px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="moderator">Moderator</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                        {member.user_id !== user?.id && (
                          <Button
                            onClick={() => removeMember(member.user_id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TeamMemberManagement;